import { FileList as FileFrameList } from 'components/common/types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import {
  DirectSalesObjectiveFrameListFilter,
  DirectSalesLineObjective,
  DirectSalesObjectiveFilterType,
  DirectSalesObjectiveTarget,
} from 'components/common/types/DirectSalesCampaign.types';

export const LINE_OBJECTIVES_LIMIT = 999;
export const OBJECTIVE_EXPLODE_COUNT_WARNING = 99;

interface ExplodeFiltersProps {
  objective: DirectSalesLineObjective;
  objectiveFilterType: DirectSalesObjectiveFilterType;
  objectivesCount: number;
}

const updateObjectiveTarget = (
  objective: DirectSalesLineObjective,
  field: 'frames' | 'impressions' | 'budget',
): DirectSalesObjectiveTarget => {
  const objectiveTarget = objective[field];

  if (!objectiveTarget?.value) return objectiveTarget;

  return { ...objectiveTarget, objectiveId: null };
};

const createExplodedFrameListFilterObjectives = (
  objective: DirectSalesLineObjective,
  frameListFilters:
    | DirectSalesObjectiveFrameListFilter['listFiles']
    | DirectSalesObjectiveFrameListFilter['routeFrameCodes'],
  makeFrameList: (filter: FileFrameList | RouteFrameCode) => {
    routeFrameCodes: RouteFrameCode[];
    listFiles: FileFrameList[];
  },
  objectivesCount: number,
): DirectSalesLineObjective[] => {
  return frameListFilters.map((filter, filterIndex) => {
    const objectiveNumber = objectivesCount + filterIndex + 1;

    return {
      ...objective,
      frames: updateObjectiveTarget(objective, 'frames'),
      impressions: updateObjectiveTarget(objective, 'impressions'),
      budget: updateObjectiveTarget(objective, 'budget'),
      frameList: makeFrameList(filter),
      objectiveGroupId: objectiveNumber,
      name: `Objective ${objectiveNumber}`,
      objectiveStatus: undefined,
    };
  });
};

const explodeFrameListFilters = (
  objective: DirectSalesLineObjective,
  objectivesCount: number,
): {
  baseExplodedFrameListObjective: DirectSalesLineObjective | undefined;
  explodedListFiles: DirectSalesLineObjective[];
  explodedRouteFrameCodes: DirectSalesLineObjective[];
} => {
  const { listFiles, routeFrameCodes } = objective.frameList;

  let baseExplodedFrameListObjective: DirectSalesLineObjective | undefined;
  let explodedListFiles: DirectSalesLineObjective[] = [];
  let explodedRouteFrameCodes: DirectSalesLineObjective[] = [];

  const [firstListFile, ...restListFiles] = listFiles;
  const [firstRouteFrameCode, ...restRouteFrameCodes] = routeFrameCodes;

  if (firstListFile) {
    baseExplodedFrameListObjective = {
      ...objective,
      frameList: {
        listFiles: [firstListFile],
        routeFrameCodes: [],
      },
    };

    explodedListFiles = createExplodedFrameListFilterObjectives(
      objective,
      restListFiles,
      (filter) => ({
        routeFrameCodes: [],
        listFiles: [filter as FileFrameList],
      }),
      objectivesCount,
    );

    explodedRouteFrameCodes = createExplodedFrameListFilterObjectives(
      objective,
      routeFrameCodes,
      (filter) => ({
        routeFrameCodes: [filter as RouteFrameCode],
        listFiles: [],
      }),
      objectivesCount + explodedListFiles.length,
    );
  } else {
    baseExplodedFrameListObjective = {
      ...objective,
      frameList: {
        routeFrameCodes: [firstRouteFrameCode],
        listFiles: [],
      },
    };

    explodedListFiles = createExplodedFrameListFilterObjectives(
      objective,
      restRouteFrameCodes,
      (filter) => ({
        routeFrameCodes: [filter as RouteFrameCode],
        listFiles: [],
      }),
      objectivesCount,
    );
  }

  return { baseExplodedFrameListObjective, explodedListFiles, explodedRouteFrameCodes };
};

export const explodeFilters = ({
  objective,
  objectiveFilterType,
  objectivesCount,
}: ExplodeFiltersProps): {
  baseExplodedObjective: DirectSalesLineObjective | undefined;
  explodedObjectives: DirectSalesLineObjective[];
} => {
  let baseExplodedObjective: DirectSalesLineObjective | undefined;
  let explodedObjectives: DirectSalesLineObjective[] = [];

  if (objectiveFilterType === 'frameList') {
    const { baseExplodedFrameListObjective, explodedListFiles, explodedRouteFrameCodes } = explodeFrameListFilters(
      objective,
      objectivesCount,
    );

    baseExplodedObjective = baseExplodedFrameListObjective;
    explodedObjectives = [...explodedListFiles, ...explodedRouteFrameCodes];
  } else {
    const [firstFilter, ...restFilters] = objective[objectiveFilterType];

    baseExplodedObjective = {
      ...objective,
      [objectiveFilterType]: [firstFilter],
    };

    explodedObjectives = restFilters.map((filter, filterIndex) => {
      const objectiveNumber = objectivesCount + filterIndex + 1;

      return {
        ...objective,
        frames: updateObjectiveTarget(objective, 'frames'),
        impressions: updateObjectiveTarget(objective, 'impressions'),
        budget: updateObjectiveTarget(objective, 'budget'),
        [objectiveFilterType]: [filter],
        objectiveGroupId: objectiveNumber,
        name: `Objective ${objectiveNumber}`,
        objectiveStatus: undefined,
      };
    });
  }

  return { baseExplodedObjective, explodedObjectives };
};
